import { GetChannelsForExcel } from "../Components/Survey/Services/ResponderService";

/**
 * How many pre-formatted data rows ship in the sample sheet.
 */
const DATA_ROWS = 1000;

const HEADER_FILL = "FF7030A0";
const HEADER_FONT = "FFFFFFFF";
const SYSTEM_FILL = "FFF2F2F2";
const SYSTEM_FONT = "FF7F7F7F";
const BAD_FILL = "FFFFC7CE";
const BAD_FONT = "FF9C0006";
const GOOD_FILL = "FFC6EFCE";
const GOOD_FONT = "FF006100";

const COLUMNS = [
    { header: "Responder e-mail *", key: "email", width: 20 },
    { header: "Phone Number", key: "phone", width: 20 },
    { header: "Responder Name", key: "name", width: 20 },
    { header: "Channel *", key: "channel", width: 20 },
    { header: "Experience Type *", key: "experience", width: 20 },
    { header: "Reference Code/ID", key: "reference", width: 20 },
    { header: "Gender", key: "gender", width: 20 },
    { header: "Location", key: "location", width: 20 },
    { header: "Channel ID (system value)", key: "channelId", width: 20 },
    { header: "Status", key: "status", width: 20 },
];

export const downloadSampleResponderSheet = async (fallbackChannels = []) => {
    try {
        let excelChannels = [];
        try {
            const responseAPI = await GetChannelsForExcel();
            if (responseAPI?.data?.status?.toLowerCase() === "success") {
                excelChannels = responseAPI.data.channels || [];
            } else {
                excelChannels = fallbackChannels;
            }
        } catch (apiError) {
            excelChannels = fallbackChannels;
        }
        const channelRows = excelChannels.length > 0 ? excelChannels : [{ value: "", id: "" }];
        const lastChannelRow = channelRows.length + 1; // data starts on row 2

        const ExcelJS = await import("exceljs");
        const workbook = new ExcelJS.Workbook();
        workbook.creator = "XPR Survey";
        workbook.created = new Date();

        const dataSheet = workbook.addWorksheet("Responder Data", {
            views: [{ state: "frozen", ySplit: 1 }],
        });

        // Set all columns with equal width of 20
        dataSheet.columns = COLUMNS.map((c) => ({ header: c.header, key: c.key, width: 20 }));

        dataSheet.getRow(1).eachCell((cell, colNumber) => {
            const isSystemCol = colNumber >= 9; // Channel ID (system) / Status
            cell.font = { bold: true, color: { argb: isSystemCol ? SYSTEM_FONT : HEADER_FONT } };
            cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: isSystemCol ? SYSTEM_FILL : HEADER_FILL },
            };
            cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
            };
            cell.alignment = { vertical: "middle" };
        });

        for (let r = 2; r <= DATA_ROWS; r++) {
            const row = dataSheet.getRow(r);

            // Apply border to all columns (A–J) in this row
            for (let c = 1; c <= COLUMNS.length; c++) {
                row.getCell(c).border = {
                    top: { style: "thin" },
                    left: { style: "thin" },
                    bottom: { style: "thin" },
                    right: { style: "thin" },
                };
            }

            const channelIdCell = row.getCell(9);
            channelIdCell.value = {
                formula: `IFERROR(INDEX('Master Data'!$B$2:$B$${lastChannelRow},MATCH(D${r},Channel,0)),"")`,
            };
            channelIdCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SYSTEM_FILL } };
            channelIdCell.font = { color: { argb: SYSTEM_FONT } };
            channelIdCell.protection = { locked: true };

            const statusCell = row.getCell(10);
            statusCell.value = {
                formula: `IF(COUNTA(A${r}:E${r})=0,"",IF(AND(A${r}<>"",C${r}<>"",D${r}<>""),"Ready","Missing required field(s)"))`,
            };
            statusCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: SYSTEM_FILL } };
        }

        // Dropdown for Channel
        dataSheet.dataValidations.add(`D2:D${DATA_ROWS}`, {
            type: "list",
            allowBlank: false,
            formulae: ["Channel"],
            showErrorMessage: true,
            errorStyle: "stop",
            errorTitle: "Invalid channel",
            error: "Please pick a channel from the dropdown list.",
        });

        // Dropdown for Experience
        dataSheet.dataValidations.add(`E2:E${DATA_ROWS}`, {
            type: "list",
            allowBlank: false,
            formulae: ["Experience"],
            showErrorMessage: true,
            errorStyle: "stop",
            errorTitle: "Invalid experience type",
            error: "Please pick an experience type from the dropdown list.",
        });

        // Dropdown for Gender (Column G)
        dataSheet.dataValidations.add(`G2:G${DATA_ROWS}`, {
            type: "list",
            allowBlank: true, // Allowing blank since Gender header doesn't have an asterisk (*)
            formulae: ["Gender"],
            showErrorMessage: true,
            errorStyle: "stop",
            errorTitle: "Invalid gender",
            error: "Please pick a valid gender (Male, Female, Other) from the dropdown list.",
        });

        const badFillRule = (formula) => ({
            type: "expression",
            formulae: [formula],
            style: {
                fill: { type: "pattern", pattern: "solid", bgColor: { argb: BAD_FILL } },
                font: { color: { argb: BAD_FONT } },
            },
        });

        dataSheet.addConditionalFormatting({
            ref: `A2:A${DATA_ROWS}`,
            rules: [badFillRule(`AND($A2="",COUNTA($A2:$E2)>0)`)],
        });
        dataSheet.addConditionalFormatting({
            ref: `C2:C${DATA_ROWS}`,
            rules: [badFillRule(`AND($C2="",COUNTA($A2:$E2)>0)`)],
        });
        dataSheet.addConditionalFormatting({
            ref: `D2:D${DATA_ROWS}`,
            rules: [badFillRule(`AND($D2="",COUNTA($A2:$E2)>0)`)],
        });
        dataSheet.addConditionalFormatting({
            ref: `J2:J${DATA_ROWS}`,
            rules: [
                badFillRule(`$J2="Missing required field(s)"`),
                {
                    type: "expression",
                    formulae: [`$J2="Ready"`],
                    style: {
                        fill: { type: "pattern", pattern: "solid", bgColor: { argb: GOOD_FILL } },
                        font: { color: { argb: GOOD_FONT } },
                    },
                },
            ],
        });

        // Hide System and Status columns
        dataSheet.getColumn(9).hidden = true;  // Channel ID (system value)
        dataSheet.getColumn(10).hidden = true; // Status

        // Setup Master Data Sheet
        const masterSheet = workbook.addWorksheet("Master Data", { state: "hidden" });
        masterSheet.getColumn(1).width = 24; // Channels
        masterSheet.getColumn(2).width = 10; // ID
        masterSheet.getColumn(4).width = 18; // Experience Type
        masterSheet.getColumn(6).width = 15; // Gender

        masterSheet.getCell("A1").value = "Channels";
        masterSheet.getCell("B1").value = "ID";
        masterSheet.getCell("D1").value = "Experience Type";
        masterSheet.getCell("F1").value = "Gender";

        channelRows.forEach((ch, index) => {
            const r = index + 2;
            masterSheet.getCell(`A${r}`).value = ch.value;
            masterSheet.getCell(`B${r}`).value = ch.id;
        });

        masterSheet.getCell("D2").value = "Fresher";
        masterSheet.getCell("D3").value = "Experienced";

        masterSheet.getCell("F2").value = "Male";
        masterSheet.getCell("F3").value = "Female";
        masterSheet.getCell("F4").value = "Other";

        // Bind Defined Names for dropdown validation
        workbook.definedNames.add(`'Master Data'!$A$2:$A$${lastChannelRow}`, "Channel");
        workbook.definedNames.add(`'Master Data'!$D$2:$D$3`, "Experience");
        workbook.definedNames.add(`'Master Data'!$F$2:$F$4`, "Gender");

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute("download", "SampleResponderSheet.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(link.href);
    } catch (error) {
        console.error("Error generating sample sheet:", error);
        if (typeof CustomToast !== "undefined") {
            // eslint-disable-next-line no-undef
            CustomToast("fail", "Failed to generate sample sheet with dynamic channels.");
        }
    }
};